import bindAllOfThis from 'src/utils/BindThisHelper';
import { EndGameScene } from 'src/games/SpacePortGame/scenes/EndGameScene';
import { TiledGameObject } from '../../shared/phaserHelpers/HandyTypes';
import { LandingPad, Size } from '../objects/LandingPad';
import { SpaceShip, SpaceShipConfig } from '../objects/SpaceShip';
import { PhaserModal, PhaserModalConfig } from 'src/games/shared/Modal';
import TranslateApi from '../../../utils/TranslateHelper';
import { PhaserButtonConfig } from 'src/games/shared/Button';
import { Color } from 'src/games/shared/ColorValue';
import { emit } from 'cluster';
import { Scene2 } from '../../demos/phaserMultiSceneDemo/scenes/scene2';

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    shipConfigs: SpaceShipConfig[];
    ships: SpaceShip[];
    landingPads: LandingPad[];
    currentShip: SpaceShip;
    currentLandingPad: LandingPad;
    cancelIcon: Phaser.GameObjects.Image;
    shipCount: number;
    finishedCount: number;

    // How many ships total in the game (AKA count of SpaceShipConfigs array)
    private TOTAL_SHIPS: number;
    // Landing Pad frame numbers that match their state
    private FUELING: number = 2;
    private UNAVAILABLE: number = 1;
    private AVAILABLE: number = 0;

    constructor() {
        super({
            key: "MainScene"
        });
    }

    preload(): void {
        this.load.tilemapTiledJSON("map", require("src/assets/qci/maps/2e_space_port.json"));
        this.load.image("planet", require("src/assets/qci/sprites/gameSpacePort/planet.png"));
        this.load.image("cancel", require("src/assets/qci/sprites/gameSpacePort/cancel.png"));
        this.load.spritesheet("largeShip", require("src/assets/qci/sprites/gameSpacePort/large_ship.png"), { frameWidth: 235, frameHeight: 240, });
        this.load.spritesheet("largePlatform", require("src/assets/qci/sprites/gameSpacePort/large_platform.png"), { frameWidth: 354, frameHeight: 250, });
        this.load.spritesheet("mediumShip", require("src/assets/qci/sprites/gameSpacePort/medium_ship.png"), { frameWidth: 168, frameHeight: 215, });
        this.load.spritesheet("mediumPlatform", require("src/assets/qci/sprites/gameSpacePort/medium_platform.png"), { frameWidth: 288, frameHeight: 215, });
        this.load.spritesheet("smallShip", require("src/assets/qci/sprites/gameSpacePort/small_ship.png"), { frameWidth: 146, frameHeight: 198, });
        this.load.spritesheet("smallPlatform", require("src/assets/qci/sprites/gameSpacePort/small_platform.png"), { frameWidth: 221, frameHeight: 181, });
        this.load.spritesheet("port", require("src/assets/qci/sprites/gameSpacePort/station_control_rooms.png"), { frameWidth: 870, frameHeight: 130, });
        this.load.spritesheet("background", require("src/assets/qci/sprites/gameSpacePort/starfield.png"), { frameWidth: 1360, frameHeight: 768, });
    }

    initialize() {
        bindAllOfThis(this, MainScene.prototype);
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: "map" });
        this.shipConfigs = [...SpaceShip.SpaceShipConfigs.reverse()];
        this.ships = [];
        this.TOTAL_SHIPS = this.shipConfigs.length;
        this.landingPads = [];
        this.shipCount = 0;
        this.finishedCount = 0;
    }

    create(): void {
        this.initialize();
        var background = this.add.sprite(0, 0, "background", 0);
        background.setOrigin(0);
        background.setDisplaySize(this.gameWidth, this.gameHeight);

        var planet = this.add.image(0, 0, "planet").setOrigin(0);

        // Twinkly stars in background
        this.anims.create({
            key: "twinkle",
            frames: this.anims.generateFrameNumbers("background", { start: 0, end: 2 }),
            frameRate: 1,
            repeat: -1
        });

        background.anims.play("twinkle");

        this.addObjects();
        this.addTimedEvents();

        this.cancelIcon = this.add.image(-100, 0, "cancel");
        this.cancelIcon.setDisplaySize(75, 75);
        this.cancelIcon.setDepth(10);
    }

    addTimedEvents() {
        // Decrease ship fuel level every 5 seconds
        this.time.addEvent({
            delay: 5000,
            callback: () => this.decreaseShipFuelLevel(),
            loop: true,
        });

        // Increase ship fuel level every 1.5 seconds when refueling
        this.time.addEvent({
            delay: 1500,
            callback: () => this.increaseShipFuelLevel(),
            loop: true,
        });
    }

    addObjects() {
        this.map.getObjectLayer("Station").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "port") {
                // Little "port" at the bottom of the page with flashy lights
                var port = this.add.sprite(this.gameWidth / 2, this.gameHeight - ob.height / 2, "port", 0);
                this.anims.create({
                    key: "flashy",
                    frames: this.anims.generateFrameNumbers("port", { start: 0, end: 2 }),
                    frameRate: 3,
                    repeat: -1
                });

                port.anims.play("flashy");
            }
            else {
                var size: Size = (ob.type === "small") ? Size.SMALL : (ob.type === "medium") ? Size.MEDIUM : Size.LARGE;
                var landingPad = new LandingPad(this, ob.x + ob.width / 2, ob.y + ob.height / 2, ob.type + "Platform", 0, size);
                landingPad.setDepth(2);
                landingPad.on("pointerdown", () => {
                    this.currentLandingPad = landingPad;
                    if (this.canLand(this.currentShip, landingPad)) {
                        this.landShip(this.currentShip, landingPad);
                        this.currentShip = null;
                    }
                });
                this.landingPads.push(landingPad);
            }
        });

        // Update landing pads every second
        this.time.addEvent({
            delay: 1000,
            callback: () => this.updateLandingPads(),
            loop: true
        });

        for (var i = 0; i < 3; i++) {
            this.addShip();
        }
    }

    addShip() {
        if (this.shipCount < this.TOTAL_SHIPS) {
            var config = this.shipConfigs.pop();
            this.createShip(config);
        }
    }

    createShip(config) {
        var ship = new SpaceShip(this, this.shipCount, 0, 0, config);
        ship.setRandomPosition(100, -200, 1200, 100); 

        // Ensures that the ship won't overlap with any others that are currently hovering
        while (this.overlaps(ship)) {
            ship.setRandomPosition(100, -200, 1200, 100); 
        }

        this.shipCount++;

        ship.on('pointerdown', () => {
            // Only allow them to click on the ship if it's hovering and it's not tweening (moving somewhere)
            if (ship.state === "hovering" && !this.tweens.isTweening(ship)) {
                this.currentShip = ship;
                // Untint all other ships
                this.ships.forEach((s) => {
                    if (s.id !== this.currentShip.id) {
                        s.clearTint();
                    }
                });
                this.updateLandingPads();
                // Tint this ship so you have some indication that it's the "currentShip"
                this.currentShip.setTint(0xb0b4ba);
            }
            else if (ship.state === "ready") {
                this.takeOff(ship);
            }
        });

        this.ships.push(ship);

        var y = Phaser.Math.Between(100, 150);

        // Have ships come down from top of screen
        this.tweens.add({
            targets: ship,
            y: y,
            ease: "Linear",
            duration: ship.size.weight * 1000
        });
    }

    decreaseShipFuelLevel() {
        this.ships.forEach((s: SpaceShip) => {
            var frame = parseInt(s.frame.name) - 1;
            // Tank is less than full
            if (frame < 8) {
                s.needsFuel = true;
            }
            if (s.state === "hovering") {
                s.setFrame(Math.min(Math.max(0, frame), 8));
            }
        });
    }

    increaseShipFuelLevel() {
        this.ships.forEach((s: SpaceShip) => {
            var frame = parseInt(s.frame.name) + 1;
            if (s.state === "fueling") {
                s.setFrame(Math.min(Math.max(0, frame), 8));

                // Tank is almost full, add another ship
                if (frame === 6) {
                    this.addShip();
                }

                // Tank is full, update state
                if (frame === 8) {
                    s.needsFuel = false;
                    s.state = "ready";
                    s.landingPad.state = "ready";
                    s.landingPad.setFrame(this.AVAILABLE);
                }
            }
        });
    }

    canLand(ship: SpaceShip, landingPad: LandingPad) {
        if (ship == null || landingPad == null) {
            return false;
        }
        // Can only land if landing pad is empty
        if (landingPad.state === "empty" && landingPad.size.weight >= ship.size.weight) {
            return true;
        }
        else {
            // If they could land but the ship is too big, show some feedback
            if (landingPad.state === "empty" && landingPad.size.weight < ship.size.weight) {
                this.feedback("SpacePort.Phaser.tooSmall", landingPad);
            }
            return false;
        }
    }

    /**
     * Update landing pad frames (the color of the light on the landing pad that indicates it's state)
     */
    updateLandingPads() {
        this.landingPads.forEach((lp: LandingPad) => {
            // First check if it's currently fueling
            if (lp.state === "fueling") {
                lp.setFrame(this.FUELING);
            }
            // Otherwise, check it's availability
            else {
                if (lp.state === "empty" || lp.state === "ready") {
                    if (this.currentShip != null) {
                        if (lp.size.weight >= this.currentShip.size.weight) {
                            lp.setFrame(this.AVAILABLE);
                        }
                        else {
                            lp.setFrame(this.UNAVAILABLE);
                        }
                    }
                    else {
                        lp.setFrame(this.AVAILABLE);
                    }
                }
            }
        });
    }


    /**
     * Lands the ship using nested tweens
     * First tween moves the ship above the landing pad (navigating from somewhere to the correct location)
     * Second tween moves it directly down on top of the pad (actually landing it)
     */
    landShip(ship: SpaceShip, landingPad: LandingPad) {
        var abovePad = landingPad.getTopLeft().y - ship.height * 1.2;
        var onPad = landingPad.getTopLeft().y - ship.height / 4;

        // Change the state of the landing pad to waiting because it's no longer available, it's waiting for 
        // a ship to land there (needed because of the tweens)
        landingPad.state = "waiting";
        ship.clearTint();

        var distance = ship.x - landingPad.x;
        var duration = Math.min(3000, (Math.sqrt(distance * distance) / ((4 - ship.size.weight) * 100)) * 1000);

        this.tweens.add({
            targets: ship,
            // duration: duration,
            x: landingPad.x,
            y: abovePad,
            ease: "Linear",
            onComplete: () => {
                // Vertically land the ship on the pad
                this.tweens.add({
                    targets: ship,
                    duration: 1000,
                    y: onPad,
                    ease: "Linear",
                    onComplete: () => {
                        landingPad.state = "fueling";
                        ship.landingPad = landingPad;
                        ship.state = "landed";
                        landingPad.setFrame(this.FUELING);
                        ship.state = "fueling";
                    }
                });
            }
        });
    }

    takeOff(ship: SpaceShip) {
        this.tweens.add({
            targets: ship,
            duration: ship.size.weight * 1000,
            y: -100,
            ease: "Linear",
            onStart: () => {
                ship.landingPad.state = "empty";
                ship.landingPad.setFrame(this.AVAILABLE);
            },
            onComplete: () => {
                ship.destroy();
                this.finishedCount++;
                this.checkIfGameOver();
            }
        });
    }

     /**
     * Returns true if the current ship will end up overlapping with another hovering ship
     */
    overlaps(ship: SpaceShip): boolean {
        var hovering = this.ships.filter((s) => s.state === "hovering" && s.id !== ship.id);
        for (var h of hovering) {
            var tempRect = h.getBounds();
            // Match their y values (don't care about y values because ships come from above anyway)
            tempRect.y = ship.y;
            // Check if they intersect
            if (Phaser.Geom.Intersects.RectangleToRectangle(tempRect, ship.getBounds())) {
                return true;
            }
        }
        return false;
    }

    modal(translationKey: string) {
        var text = TranslateApi.translate(translationKey);
        var buttonConfig: PhaserButtonConfig = {
            smartSize: true,
            backgroundColor: Color.KUDER_PRIMARY_BUTTON
        };

        var config: PhaserModalConfig = {
            title: text,
            buttonWidth: 200,
            buttonConfig: buttonConfig
        };

        var buttonText = TranslateApi.translate("common.ok");
        var options = [{ id: 1, displayText: buttonText }];
        var modal = new PhaserModal(this, 0, 0, options, config);
        modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2);
        modal.on("optionClicked", () => {
            modal.destroy();
        });
    }

    feedback(key: string, landingPad: LandingPad) {
        var text = TranslateApi.translate(key);
        var style = {
            fontSize: 20,
            fontFamily: "Russo One, Arial",
            backgroundColor: "#000000",
            padding: 10
        };
        var blurb = this.add.text(landingPad.x, landingPad.y - landingPad.height / 2 - 50, text, style);
        blurb.setOrigin(0.5);

        this.tweens.add({
            targets: [blurb, this.cancelIcon],
            duration: 3000,
            alpha: {
                getStart: () => 1,
                getEnd: () => 0
            },
            onStart: () => {
                this.cancelIcon.setPosition(landingPad.x, landingPad.y - landingPad.height / 4);
            },
            ease: "Linear",
            onComplete: () => {
                blurb.destroy();
            }
        });
    }

    checkIfGameOver() {
        if (this.finishedCount === this.shipCount) {
            this.endGame();
        }
    }

    endGame(): void {
        this.input.setDefaultCursor("default");
        this.input.removeAllListeners();
        this.scene.add("EndGameScene", EndGameScene, true);
        this.scene.remove(this);
    }

   

}



// WEBPACK FOOTER //
// ./src/games/SpacePortGame/scenes/MainScene.ts